import type { GetStandardQuestionnaireSummaryReportQuery } from '@report/graphql';

import {
  AGE_LABELS,
  buildSection,
  EDUCATION_LABELS,
  ORGANIZATION_LABELS,
  type QuestionnaireReportSection,
  RATING_LABELS,
  type SectionConfig,
} from '../questionnaire-report-shared';

export type StandardTemplateSection = QuestionnaireReportSection;

type StandardQuestionnaireSummaryReport =
  GetStandardQuestionnaireSummaryReportQuery['reportGetStandardQuestionnaireSummaryReport'];

// The Standard exam form only offers these three major options, so the report
// mirrors exactly that set (matching the "Template 1 – Standard" design).
export const MAJOR_LABELS: Record<string, string> = {
  'financial-related':
    'สาขาการเงิน / การเงินและการธนาคาร / วิศวกรรมการเงิน / หรือสาขาที่เกี่ยวข้องกับการเงินโดยตรง',
  'accounting-related': 'สาขาเศรษฐศาสตร์ / เศรษฐศาสตร์ธุรกิจ / บัญชี / บริหารธุรกิจ',
  other: 'อื่น ๆ โปรดระบุ',
};

export const WORK_EXPERIENCE_LABELS: Record<string, string> = {
  none: 'ไม่มีประสบการณ์ทำงานในธุรกิจหลักทรัพย์',
  'less-than-1-year': 'น้อยกว่า 1 ปี',
  '1-to-3-years': '1 - 3 ปี',
  '4-to-7-years': '4 - 7 ปี',
  'more-than-7-years': 'มากกว่า 7 ปี',
};

export const EXAM_ATTEMPTED_LABELS: Record<string, string> = {
  never: 'ไม่เคย',
  'more-than-zero': 'เคย (ระบุจำนวนครั้ง)',
};

export const EXAM_PREPARATION_LABELS: Record<string, string> = {
  'prepare-exam-by-self': 'เตรียมตัวสอบด้วยตนเอง',
  'attend-training-from-official':
    'เข้าอบรมหลักสูตรเตรียมสอบที่จัดโดยหน่วยงานต้นสังกัด / มหาวิทยาลัย',
  'attend-training-from-training-institution': 'เข้าอบรมหลักสูตรเตรียมสอบกับสถาบันอบรม / ติวเตอร์',
  'attend-specific-program': 'เข้าอบรมในโครงการเฉพาะ เช่น New Breed, AISA-CISA Scholarship',
  other: 'อื่น ๆ โปรดระบุ',
};

// The Standard exam form's objectives (no `career-opportunity`), in the order
// shown in the "Template 1 – Standard" design.
export const STANDARD_OBJECTIVE_LABELS: Record<string, string> = {
  'register-license-and-profession': 'เพื่อขึ้นทะเบียนใบอนุญาตฯ',
  'self-assessment': 'เพื่อประเมินความรู้ตนเอง',
  'special-project-with-outside-institution':
    'สอบตามโครงการเฉพาะของหน่วยงานภายนอก / สถาบันการศึกษา',
  other: 'อื่น ๆ โปรดระบุ',
};

type SectionField = Exclude<keyof StandardQuestionnaireSummaryReport, '__typename'>;

const TOP_SECTION_CONFIGS: SectionConfig<SectionField>[] = [
  { key: 'section-1', variant: 'standard', title: '1 อายุ', field: 'age', labelMap: AGE_LABELS },
  {
    key: 'section-2',
    variant: 'standard',
    title: '2 ระดับการศึกษาสูงสุด',
    field: 'highestEducationLevel',
    labelMap: EDUCATION_LABELS,
  },
  {
    key: 'section-3',
    variant: 'standard',
    title: '3 ภูมิหลังเกี่ยวกับสาขาที่กำลังศึกษา หรือจบการศึกษาล่าสุด',
    field: 'major',
    labelMap: MAJOR_LABELS,
  },
  {
    key: 'section-4',
    variant: 'standard',
    title: '4 ประเภทหน่วยงานที่ท่านทำงานอยู่ในปัจจุบัน',
    field: 'currentOrganization',
    labelMap: ORGANIZATION_LABELS,
  },
  {
    key: 'section-5',
    variant: 'standard',
    title: '5 ประสบการณ์ทำงานที่เกี่ยวข้องกับวิชาชีพในธุรกิจหลักทรัพย์',
    field: 'workExperience',
    labelMap: WORK_EXPERIENCE_LABELS,
  },
  {
    key: 'section-6',
    variant: 'standard',
    title: '6 ท่านเคยสอบหลักสูตรนี้มาก่อนหรือไม่',
    field: 'examAttempted',
    labelMap: EXAM_ATTEMPTED_LABELS,
  },
  {
    key: 'section-7',
    variant: 'standard',
    title: '7 วิธีการในการเตรียมตัวสอบ',
    field: 'examPreparations',
    labelMap: EXAM_PREPARATION_LABELS,
  },
  {
    key: 'section-8',
    variant: 'standard',
    title: '8 วัตถุประสงค์ของการเข้าสอบ',
    field: 'examAttendingObjectives',
    labelMap: STANDARD_OBJECTIVE_LABELS,
  },
];

const SATISFACTION_SUBSECTION_CONFIGS: SectionConfig<SectionField>[] = [
  {
    key: 'section-9-1',
    variant: 'standard',
    title: '9.1 ด้านสถานที่',
    field: 'locationSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-9-2',
    variant: 'standard',
    title: '9.2 ด้านการให้บริการของเจ้าหน้าที่',
    field: 'staffServicesSatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-9-3',
    variant: 'standard',
    title: '9.3 ด้านระบบจัดทดสอบ — ความสะดวกและเข้าใจง่ายในการใช้งาน',
    field: 'systemUsabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
  {
    key: 'section-9-4',
    variant: 'standard',
    title: '9.4 ด้านระบบจัดทดสอบ — ความเสถียรและความต่อเนื่องของระบบ',
    field: 'systemStabilitySatisfaction',
    labelMap: RATING_LABELS,
  },
];

/**
 * Builds the ordered Standard Form sections. When `report` is undefined
 * (query still loading), a placeholder scaffold is returned so the table
 * stays visible and the loading skeleton can render.
 *
 * Every section is restricted to its known option set (`restrictToKnownOptions`),
 * so the report shows exactly the options in the "Template 1 – Standard" design
 * even though the backend buckets some questions (e.g. `major`) by a wider
 * taxonomy — any option key outside the label map is dropped.
 */
export function buildStandardFormSections(
  report?: StandardQuestionnaireSummaryReport,
): StandardTemplateSection[] {
  const restrict = (config: SectionConfig<SectionField>): SectionConfig<SectionField> => ({
    ...config,
    restrictToKnownOptions: true,
  });

  return [
    ...TOP_SECTION_CONFIGS.map((config) => buildSection(restrict(config), report)),
    {
      key: 'section-9',
      variant: 'rating',
      title: '9 ความพึงพอใจต่อการใช้บริการสอบ',
      rows: [],
      subSections: SATISFACTION_SUBSECTION_CONFIGS.map((config) =>
        buildSection(restrict(config), report),
      ),
    },
  ];
}
